import React from "react";
import { useTranslation } from "react-i18next";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import { plannedBlockTitle } from "../../utils/planningSubgroups";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedExerciseName, localizedTypeName } from "../../utils/localizedCatalogFields";
import {
  resolveExerciseStateIdFromRow,
  TE_STATE_FINALIZED,
} from "../../utils/coachExerciseNavigation";

function shortBlockName(ex, lng, fallback) {
  if (!ex) return fallback;
  const name =
    localizedExerciseName(ex, lng) ||
    plannedBlockTitle(ex, lng) ||
    localizedTypeName(ex.type_ex, lng) ||
    "";
  if (!name) return fallback;
  return name.length > 18 ? `${name.slice(0, 16)}…` : name;
}

/**
 * Always-visible session big-picture: named training blocks + open full plan.
 */
export function LiveTrainingProgressRail({
  current,
  total,
  finalizedCount,
  exercises = null,
  realtimeByTeId = {},
  reviewEnabled = false,
  onReviewExercise = null,
  onSelectExercise = null,
  selectable = false,
  onOpenPlan = null,
}) {
  const { t, i18n } = useTranslation();
  const lng = currentAppLanguage(i18n);
  const pct = total > 0 ? Math.round((finalizedCount / total) * 100) : 0;
  const rows = Array.isArray(exercises) && exercises.length ? exercises : null;

  return (
    <div className="lt-progress" aria-label={t("liveTrainingProgress")}>
      <div className="lt-progress__meta">
        <span className="lt-progress__label">
          {t("liveTrainingExerciseOf", { current: current + 1, total })}
        </span>
        <span className="lt-progress__meta-right">
          <span className="lt-progress__pct">{pct}%</span>
          {typeof onOpenPlan === "function" ? (
            <button
              type="button"
              className="lt-progress__plan-btn"
              data-tour-id="live-training-open"
              onClick={onOpenPlan}
              aria-label={t("liveTrainingPlanTitle", "Session plan")}
              title={t("liveTrainingPlanTitle", "Session plan")}
            >
              <ViewAgendaOutlinedIcon style={{ fontSize: 16 }} aria-hidden />
              {t("liveTrainingPlanShort", "Plan")}
            </button>
          ) : null}
        </span>
      </div>
      <div className="lt-progress__track">
        <div className="lt-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <div
        className={`lt-progress__segments${
          rows ? " lt-progress__segments--named" : ""
        }`}
      >
        {Array.from({ length: total }).map((_, i) => {
          const ex = rows?.[i] || null;
          const sid = ex
            ? resolveExerciseStateIdFromRow(ex, realtimeByTeId)
            : null;
          const done =
            sid != null ? sid === TE_STATE_FINALIZED : i < finalizedCount;
          const canReview =
            reviewEnabled &&
            typeof onReviewExercise === "function" &&
            done &&
            ex;
          const canSelect =
            selectable &&
            typeof onSelectExercise === "function" &&
            ex &&
            !canReview;
          const className = [
            "lt-progress__seg",
            rows ? "lt-progress__seg--named" : "",
            done ? "lt-progress__seg--done" : "",
            i === current ? "lt-progress__seg--current" : "",
            canReview ? "lt-progress__seg--review" : "",
            canSelect ? "lt-progress__seg--select" : "",
            rows ? "lt-progress__seg--num" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const exerciseName = shortBlockName(ex, lng, "");
          const numberLabel = String(i + 1);
          const label = numberLabel;
          const tip = exerciseName
            ? `${numberLabel}. ${exerciseName}`
            : numberLabel;
          if (canReview) {
            return (
              <button
                key={ex.te_id || i}
                type="button"
                className={className}
                onClick={() => onReviewExercise(ex)}
                aria-label={t("demoReviewFinalizedHint")}
                title={tip}
              >
                {rows ? <span className="lt-progress__seg-label">{label}</span> : null}
              </button>
            );
          }
          if (canSelect) {
            return (
              <button
                key={ex.te_id || i}
                type="button"
                className={className}
                onClick={() => onSelectExercise(ex)}
                aria-label={
                  t("liveTrainingSelectBlock", {
                    number: i + 1,
                    defaultValue: `Go to block ${i + 1}`,
                  })
                }
                title={tip}
              >
                {rows ? <span className="lt-progress__seg-label">{label}</span> : null}
              </button>
            );
          }
          return (
            <span key={ex?.te_id || i} className={className} title={tip}>
              {rows ? <span className="lt-progress__seg-label">{label}</span> : null}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default LiveTrainingProgressRail;
